// src/app/api/adminMessagesApi.ts
//
// RTK Query slice for all /api/admin/messages/* endpoints.

import { baseApi } from "./baseApi";

/* ─────────────────────────────────────────────────────────────────
   TYPES
───────────────────────────────────────────────────────────────── */

export type MessageDirection = "inbound" | "outbound";

/** One row in the inbox list — latest message per thread */
export interface AdminThreadPreview {
  threadId: string;
  subject: string;
  lastMessage: string; // truncated body preview
  lastMessageAt: string; // ISO
  lastSenderName: string;
  lastSenderRole: string;
  lastDirection: MessageDirection;
  parentId: string;
  parentName: string;
  parentEmail: string | null;
  unreadFromParent: number; // unread inbound messages in this thread
  totalMessages: number;
  child: string | null;
}

/** A single message within a thread */
export interface AdminThreadMessage {
  id: string;
  threadId: string;
  senderName: string;
  senderRole: string;
  direction: MessageDirection;
  subject: string;
  body: string;
  readByParent: boolean;
  readAt: string | null;
  child: string | null;
  createdAt: string;
}

/** Parent info returned alongside the thread messages */
export interface ThreadParent {
  id: string;
  name: string;
  firstName: string;
  lastName: string;
  email: string;
  avatar: string | null;
}

export interface GetThreadResponse {
  success: boolean;
  count: number;
  parent: ThreadParent | null;
  data: AdminThreadMessage[];
}

export interface GetInboxResponse {
  success: boolean;
  total: number;
  page: number;
  pages: number;
  count: number;
  data: AdminThreadPreview[];
}

export interface UnreadCountResponse {
  success: boolean;
  data: { unreadThreads: number };
}

export interface ReplyResponse {
  success: boolean;
  message: string;
  data: AdminThreadMessage;
}

export interface GetInboxParams {
  status?: "unread" | "all";
  page?: number;
  limit?: number;
}

/* ─────────────────────────────────────────────────────────────────
   RTK QUERY SLICE
───────────────────────────────────────────────────────────────── */

const adminMessagesApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // GET /api/admin/messages
    adminGetMessages: builder.query<GetInboxResponse, GetInboxParams>({
      query: ({ status, page = 1, limit = 25 } = {}) => {
        const params = new URLSearchParams();
        if (status) params.set("status", status);
        params.set("page", String(page));
        params.set("limit", String(limit));
        return `/admin/messages?${params.toString()}`;
      },
      providesTags: [{ type: "AdminMessages" as const, id: "LIST" }],
    }),

    // GET /api/admin/messages/unread-count
    adminGetUnreadCount: builder.query<UnreadCountResponse, void>({
      query: () => "/admin/messages/unread-count",
      providesTags: [{ type: "AdminMessages" as const, id: "UNREAD" }],
    }),

    // GET /api/admin/messages/search?q=
    adminSearchMessages: builder.query<
      { success: boolean; count: number; data: AdminThreadPreview[] },
      string
    >({
      query: (q) => `/admin/messages/search?q=${encodeURIComponent(q)}`,
      providesTags: [{ type: "AdminMessages" as const, id: "SEARCH" }],
    }),

    // GET /api/admin/messages/thread/:threadId
    adminGetThread: builder.query<GetThreadResponse, string>({
      query: (threadId) => `/admin/messages/thread/${threadId}`,
      providesTags: (_result, _err, id) => [
        { type: "AdminMessages" as const, id },
      ],
    }),

    // POST /api/admin/messages/thread/:threadId/reply
    adminReplyToThread: builder.mutation<
      ReplyResponse,
      { threadId: string; body: string }
    >({
      query: ({ threadId, body }) => ({
        url: `/admin/messages/thread/${threadId}/reply`,
        method: "POST",
        body: { body },
      }),
      invalidatesTags: (_result, _err, { threadId }) => [
        { type: "AdminMessages" as const, id: "LIST" },
        { type: "AdminMessages" as const, id: "UNREAD" },
        { type: "AdminMessages" as const, id: threadId },
      ],
    }),

    // PATCH /api/admin/messages/thread/:threadId/read
    adminMarkThreadRead: builder.mutation<
      { success: boolean; updated: number },
      string
    >({
      query: (threadId) => ({
        url: `/admin/messages/thread/${threadId}/read`,
        method: "PATCH",
      }),
      invalidatesTags: (_result, _err, threadId) => [
        { type: "AdminMessages" as const, id: "LIST" },
        { type: "AdminMessages" as const, id: "UNREAD" },
        { type: "AdminMessages" as const, id: threadId },
      ],
    }),
  }),
  overrideExisting: false,
});

export const {
  useAdminGetMessagesQuery,
  useAdminGetUnreadCountQuery,
  useAdminSearchMessagesQuery,
  useAdminGetThreadQuery,
  useAdminReplyToThreadMutation,
  useAdminMarkThreadReadMutation,
} = adminMessagesApi;
